import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { useState } from "react";

export interface CreatableSelectOption {
  label: string;
  value: string;
}

interface CreatableMultiSelectProps {
  value?: string[];
  options: CreatableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  onChange: (value?: string[]) => void;
}

export const CreatableMultiSelect = ({
  value = [],
  options: propsOptions,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  onChange,
}: CreatableMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState(propsOptions);

  const selectedOptions = options.filter((o) => value.includes(o.value));

  const normalized = search.trim().toLowerCase();

  const exactMatch = options.some(
    (o) => o.label.toLowerCase() === normalized || o.value.toLowerCase() === normalized,
  );

  const onSelectNewOption = () => {
    const newValue = search.trim();

    setOptions(options.concat([{ value: newValue, label: newValue }]));

    onChange([...(value || []), newValue]);
    setSearch("");
  };

  const toggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];

    onChange(newValue.length > 0 ? newValue : []);
  };

  const removeOption = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newValue = value.filter((v) => v !== optionValue);
    onChange(newValue.length > 0 ? newValue : []);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between w-full h-auto min-h-10"
        >
          <div className="flex flex-wrap gap-2 flex-1 p-1">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <Badge key={option.value} variant="secondary" className="mr-1">
                  {option.label}
                  <button
                    type="button"
                    className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        // eslint-disable-next-line
                        removeOption(option.value, e as any);
                      }
                    }}
                    onMouseDown={(e) => removeOption(option.value, e)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>

          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[320px] p-0">
        <Command shouldFilter>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search ?? ""}
            onValueChange={setSearch}
          />

          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>

            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => toggleOption(option.value)}
                >
                  <Check
                    className={cn(
                      "mr-2 size-4",
                      value.includes(option.value) ? "opacity-100" : "opacity-0",
                    )}
                  />

                  {option.label}
                </CommandItem>
              ))}

              {normalized && !exactMatch && (
                <CommandItem value={`create-${normalized}`} onSelect={onSelectNewOption}>
                  <Plus className="mr-2 size-4" />
                  Use "{search.trim()}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
